import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import { getCurrentAppRoutePath, isAppRoutePath } from '@/utils/app-path';
import { clearAuthCookie, clearAuthStorage, getAuthToken } from '@/utils/auth';
import { isDevOpsApiUrl, resolveDevOpsApiUrl } from '@/utils/devOpsApiBase';

const LOGIN_PATH = '/user/login';
const AUTH_EXPIRED_CODE = 401;
const AUTH_EXPIRED_ERROR = 'AuthExpiredError';
let authRedirecting = false;

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

type AnyRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is AnyRecord =>
  typeof value === 'object' && value !== null;

const parseCode = (code: unknown): number | undefined => {
  if (typeof code === 'number') {
    return code;
  }
  if (typeof code === 'string' && code.trim()) {
    const parsed = Number(code);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const getBizCode = (payload: unknown): number | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }
  return parseCode(payload.code);
};

const redirectToLogin = () => {
  clearAuthStorage();

  if (authRedirecting) {
    return;
  }

  authRedirecting = true;
  window.setTimeout(() => {
    authRedirecting = false;
  }, 1000);

  const redirect = getCurrentAppRoutePath();
  history.replace({
    pathname: LOGIN_PATH,
    search: new URLSearchParams({ redirect }).toString(),
  });
};

const createAuthExpiredError = () => {
  const error = new Error('登录已过期');
  error.name = AUTH_EXPIRED_ERROR;
  return error;
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (success === false) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (error?.name === AUTH_EXPIRED_ERROR) {
        return;
      }
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        message.error(
          '网络异常，请检查 dev-ops 代理地址是否可访问（.env.dev）',
        );
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const originalUrl = typeof config.url === 'string' ? config.url : '';
      const rewrittenUrl = isDevOpsApiUrl(originalUrl)
        ? resolveDevOpsApiUrl(originalUrl)
        : originalUrl;

      const headers: Record<string, any> = {
        ...(config.headers as Record<string, any>),
      };

      clearAuthCookie();

      const token = getAuthToken();
      if (!token) {
        return {
          ...config,
          url: rewrittenUrl,
          credentials: 'omit',
          withCredentials: false,
          headers,
        };
      }

      const tokenValue = token.trim();
      if (!tokenValue) {
        return {
          ...config,
          url: rewrittenUrl,
          credentials: 'omit',
          withCredentials: false,
          headers,
        };
      }

      return {
        ...config,
        url: rewrittenUrl,
        credentials: 'omit',
        withCredentials: false,
        headers: {
          ...headers,
          'SFD-DEVOPS-TOKEN': tokenValue,
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const code = getBizCode(response?.data);
      if (code === AUTH_EXPIRED_CODE) {
        if (!isAppRoutePath(window.location.pathname, LOGIN_PATH)) {
          redirectToLogin();
          return Promise.reject(createAuthExpiredError());
        }
      }
      return response;
    },
  ],
};
